import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Anchor,
    Badge,
    Box,
    Button,
    Card,
    Group,
    Stack,
    Text,
    ThemeIcon,
    useMantineTheme
} from "@mantine/core";
import {IconExternalLink} from "@tabler/icons-react";
import type {Venture} from "../../ConfigContext";

/**
 * The card body navigates to the venture's page on click, but the outbound link
 * to the live product is a real anchor. That is why this is a div with an
 * onClick rather than a Link — an anchor nested inside an anchor is invalid
 * HTML, and open-in-new-tab stops working on the inner one.
 *
 * The preview is a screenshot of the venture's own site under
 * public/ventures/<slug>.jpg; if it is missing the card drops it and still
 * reads correctly.
 */
const VentureCard = ({venture}: { venture: Venture }) => {
    const theme = useMantineTheme();
    const navigate = useNavigate();
    const [previewFailed, setPreviewFailed] = useState(false);

    const showPreview = !previewFailed;

    return (
        <Card
            withBorder
            radius="lg"
            padding="lg"
            shadow="sm"
            style={{height: '100%', cursor: 'pointer'}}
            onClick={() => navigate(venture.route)}
        >
            <Stack gap="sm" h="100%">
                {showPreview && (
                    <Card.Section p="xs">
                        <Box
                            style={{
                                width: '100%',
                                aspectRatio: '16 / 10',
                                borderRadius: 'var(--mantine-radius-md)',
                                overflow: 'hidden',
                                border: `1px solid ${theme.colors.gray[4]}`
                            }}
                        >
                            <img
                                src={`/ventures/${venture.slug}.jpg`}
                                alt={`${venture.name} website`}
                                loading="lazy"
                                onError={() => setPreviewFailed(true)}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    // Anchored to the top so the site's own hero
                                    // shows, not the middle of its page.
                                    objectFit: 'cover',
                                    objectPosition: 'top center',
                                    display: 'block'
                                }}
                            />
                        </Box>
                    </Card.Section>
                )}

                <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <ThemeIcon variant="light" color="brand" size={52} radius="xl">
                        {React.createElement(venture.icon, {
                            size: 28,
                            stroke: 1.5,
                            color: theme.colors.brand[6]
                        })}
                    </ThemeIcon>
                    {venture.status && (
                        <Badge color="accent" variant="light">{venture.status}</Badge>
                    )}
                </Group>

                <Text fw={700} size="xl">{venture.name}</Text>
                <Text fw={600} c={theme.colors.brand[6]}>{venture.tagline}</Text>
                <Text size="sm" c="dimmed" style={{flexGrow: 1}}>{venture.summary}</Text>

                <Group gap="md" mt="sm" wrap="nowrap" justify="space-between">
                    <Button variant="light" color="brand" radius="xl" style={{flexShrink: 0}}>
                        Learn more
                    </Button>
                    {venture.website && (
                        <Anchor
                            href={venture.website.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            // Without this the card's own onClick also fires and
                            // races the new tab with an internal navigation.
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Group gap={5} wrap="nowrap" align="center">
                                {venture.website.label}
                                <IconExternalLink size={13} stroke={1.6}/>
                            </Group>
                        </Anchor>
                    )}
                </Group>
            </Stack>
        </Card>
    );
};

export default VentureCard;
